/**
 * "Shared with" strip for a case — the visible half of the
 * case-participants concept. Restricted client roles (project managers /
 * analysts) only see cases they own or that were shared with them, so
 * this is where an admin widens access: chips for current participants
 * plus an add-teammate control (client admins and GTI admins only — the
 * API enforces the same rule).
 *
 * A share reaches everything below it, so callers anchor on the top of
 * the case: the enquiry, else the request, else the report. Shares made
 * higher up still show here, marked with where they sit and without a
 * remove button — they are managed there, since removing one would also
 * unshare everything else beneath it.
 */

import { FormEvent, useState } from 'react';

import { ActionIcon, Button, Group, Pill, Popover, Select, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUserPlus, IconUsers } from '@tabler/icons-react';

import { useAuth } from '@/contexts/AuthContext';
import { useCaseParticipants } from '@/hooks/useCaseParticipants';
import { useCaseTeamMembers } from '@/hooks/useCaseTeamMembers';
import { ShareAnchor, ShareSource } from '@/types/report-request';

/** Client roles that only see their own and shared cases; every other
 * client role manages sharing (matching the API's rule). */
const RESTRICTED_CLIENT_ROLES = new Set(['project_manager', 'analyst']);

const SHARE_ERROR_TITLE: Record<ShareAnchor['kind'], string> = {
  enquiry: 'Could not share the enquiry',
  request: 'Could not share the request',
  report: 'Could not share the report',
};

const INHERITED_LABEL: Record<Exclude<ShareSource, 'direct'>, string> = {
  request: 'via request',
  enquiry: 'via enquiry',
};

interface CaseSharingProps {
  anchor: ShareAnchor;
}

export function CaseSharing({ anchor }: Readonly<CaseSharingProps>) {
  const { user } = useAuth();
  const { participants, addParticipant, removeParticipant } = useCaseParticipants(anchor);
  const [opened, setOpened] = useState(false);
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  // GTI admins and every client who is not a restricted role manage
  // sharing; restricted roles see the chips read-only.
  const canManage =
    !!user &&
    (user.isAdministrator ||
      (user.isClient && !RESTRICTED_CLIENT_ROLES.has(user.clientRole ?? '')));

  // Member options load lazily on first popover open. When the lookup
  // fails (Backoffice bridge down) the form degrades to free-text email
  // entry — the add call validates membership server-side either way.
  const {
    members,
    error: membersError,
    isLoading: membersLoading,
  } = useCaseTeamMembers(anchor, Boolean(opened && canManage));
  const participantEmails = new Set(participants.map((p) => p.email.toLowerCase()));
  const memberOptions = members
    .filter((m) => m.email && !participantEmails.has(m.email.toLowerCase()))
    .map((m) => {
      const name = `${m.firstName} ${m.lastName}`.trim();
      return {
        value: m.email as string,
        label: name ? `${name} (${m.email})` : (m.email as string),
      };
    });
  const usePicker = !membersError && (membersLoading || members.length > 0);

  const handleAdd = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      return;
    }
    setSaving(true);
    try {
      await addParticipant(trimmed);
      setEmail('');
      setOpened(false);
    } catch (err) {
      notifications.show({
        title: SHARE_ERROR_TITLE[anchor.kind],
        message:
          err instanceof Error && err.message
            ? err.message
            : 'Check the email belongs to your team and try again.',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (participantId: string) => {
    try {
      await removeParticipant(participantId);
    } catch (err) {
      notifications.show({
        title: 'Could not remove the teammate',
        message: err instanceof Error ? err.message : 'Please try again.',
        color: 'red',
      });
    }
  };

  if (participants.length === 0 && !canManage) {
    return null;
  }

  return (
    <Group gap={6} wrap="wrap" data-testid="case-sharing">
      <Group gap={4} wrap="nowrap">
        <IconUsers size={14} />
        <Text size="sm" c="dimmed">
          Shared with
        </Text>
      </Group>
      {participants.length === 0 && (
        <Text size="sm" c="dimmed" fs="italic">
          only you and team admins
        </Text>
      )}
      {participants.map((p) => {
        const inherited = p.source === 'direct' ? null : INHERITED_LABEL[p.source];
        return (
          <Pill
            key={p.id}
            size="sm"
            withRemoveButton={canManage && !inherited}
            onRemove={() => void handleRemove(p.id)}
            data-testid={`case-participant-${p.email}`}
          >
            {p.email}
            {inherited && (
              <Text span size="xs" c="dimmed" ml={4}>
                {inherited}
              </Text>
            )}
          </Pill>
        );
      })}
      {canManage && (
        <Popover opened={opened} onChange={setOpened} width={300} position="bottom-start">
          <Popover.Target>
            <ActionIcon
              variant="light"
              size="sm"
              aria-label="Share with a teammate"
              onClick={() => setOpened((o) => !o)}
              data-testid="case-sharing-add"
            >
              <IconUserPlus size={14} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <form onSubmit={(e) => void handleAdd(e)}>
              <Group gap="xs" wrap="nowrap" align="flex-end">
                {usePicker ? (
                  <Select
                    label="Teammate"
                    placeholder={membersLoading ? 'Loading team…' : 'Pick a teammate'}
                    size="xs"
                    data={memberOptions}
                    value={email || null}
                    onChange={(value) => setEmail(value ?? '')}
                    searchable
                    clearable
                    nothingFoundMessage="No teammates left to add"
                    disabled={membersLoading}
                    style={{ flex: 1 }}
                    data-testid="case-sharing-member"
                  />
                ) : (
                  <TextInput
                    label="Teammate's email"
                    placeholder="name@company.com"
                    type="email"
                    size="xs"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    style={{ flex: 1 }}
                    data-testid="case-sharing-email"
                  />
                )}
                <Button type="submit" size="xs" loading={saving}>
                  Share
                </Button>
              </Group>
            </form>
          </Popover.Dropdown>
        </Popover>
      )}
    </Group>
  );
}
